import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatBRL, formatDate, formatDateTime, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, Envelope, Phone, IdentificationCard, FilePdf, Paperclip } from "@phosphor-icons/react";
import Attachments from "@/components/Attachments";

const INSP_STATUS = {
  scheduled: { label: "Agendada", cls: "bg-accent/20 text-accent" },
  in_progress: { label: "Em execução", cls: "bg-primary/20 text-primary" },
  completed: { label: "Concluída", cls: "bg-green-500/20 text-green-400" },
  cancelled: { label: "Cancelada", cls: "bg-destructive/20 text-destructive" },
};
const INV_STATUS = {
  pending: { label: "Pendente", cls: "bg-accent/20 text-accent" },
  paid: { label: "Pago", cls: "bg-green-500/20 text-green-400" },
  overdue: { label: "Vencido", cls: "bg-destructive/20 text-destructive" },
  cancelled: { label: "Cancelado", cls: "bg-muted text-muted-foreground" },
};
const DOC_STATUS = {
  draft: { label: "Rascunho", cls: "bg-muted text-muted-foreground" },
  sent: { label: "Enviado", cls: "bg-accent/20 text-accent" },
  approved: { label: "Aprovado", cls: "bg-green-500/20 text-green-400" },
  signed: { label: "Assinado", cls: "bg-primary/20 text-primary" },
  rejected: { label: "Recusado", cls: "bg-destructive/20 text-destructive" },
};

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, i, inv, d] = await Promise.all([
          api.get(`/clients/${id}`),
          api.get(`/inspections`, { params: { client_id: id } }),
          api.get(`/invoices`, { params: { client_id: id } }),
          api.get(`/documents`, { params: { client_id: id } }),
        ]);
        setClient(c.data); setInspections(i.data); setInvoices(inv.data); setDocs(d.data);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return <Layout title="Carregando..."><div className="label-tech text-muted-foreground animate-pulse">Buscando dados...</div></Layout>;
  }
  if (!client) {
    return <Layout title="Cliente não encontrado"><Link to="/clients" className="text-accent">← Voltar</Link></Layout>;
  }

  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalPending = invoices.filter(i => i.status === "pending").reduce((s, i) => s + Number(i.amount || 0), 0);
  const contracts = docs.filter(d => d.type === "contract");
  const proposals = docs.filter(d => d.type === "proposal");

  return (
    <Layout
      title={client.name}
      subtitle={client.company || client.document || "Cliente"}
      actions={
        <Link to="/clients">
          <Button variant="outline" className="gap-2" data-testid="back-to-clients">
            <ArrowLeft size={16} weight="bold" /> Voltar
          </Button>
        </Link>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="tech-card p-6 lg:col-span-2">
          <div className="label-tech text-muted-foreground mb-4">Dados de contato</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <IdentificationCard size={18} className="text-accent mt-0.5 shrink-0" />
              <div>
                <div className="label-tech text-muted-foreground text-[10px]">CPF/CNPJ</div>
                <div className="font-bold mono">{client.document || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Envelope size={18} className="text-accent mt-0.5 shrink-0" />
              <div>
                <div className="label-tech text-muted-foreground text-[10px]">E-mail</div>
                <div className="font-bold">{client.email || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone size={18} className="text-accent mt-0.5 shrink-0" />
              <div>
                <div className="label-tech text-muted-foreground text-[10px]">Telefone</div>
                <div className="font-bold">{client.phone || "—"}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-accent mt-0.5 shrink-0" />
              <div>
                <div className="label-tech text-muted-foreground text-[10px]">Endereço</div>
                <div className="font-bold">
                  {[client.address_street, client.address_number, client.address_neighborhood, client.address_city, client.address_state]
                    .filter(Boolean).join(", ") || "—"}
                </div>
              </div>
            </div>
          </div>
          {client.notes && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="label-tech text-muted-foreground mb-1">Observações</div>
              <p className="text-sm">{client.notes}</p>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="tech-card p-5">
            <div className="label-tech text-muted-foreground">Total recebido</div>
            <div className="text-2xl font-black tracking-tight mt-1 text-green-400" style={{ fontFamily: "Manrope" }}>{formatBRL(totalPaid)}</div>
          </div>
          <div className="tech-card p-5">
            <div className="label-tech text-muted-foreground">Aguardando pagamento</div>
            <div className="text-2xl font-black tracking-tight mt-1 text-accent" style={{ fontFamily: "Manrope" }}>{formatBRL(totalPending)}</div>
          </div>
          <div className="tech-card p-5">
            <div className="label-tech text-muted-foreground">Inspeções realizadas</div>
            <div className="text-2xl font-black tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
              {inspections.filter(i => i.status === "completed").length}
              <span className="text-muted-foreground text-base ml-1">/ {inspections.length}</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="inspections" className="w-full">
        <TabsList data-testid="client-tabs">
          <TabsTrigger value="inspections" data-testid="tab-inspections">Inspeções ({inspections.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Faturas ({invoices.length})</TabsTrigger>
          <TabsTrigger value="proposals" data-testid="tab-proposals">Propostas ({proposals.length})</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contratos ({contracts.length})</TabsTrigger>
          <TabsTrigger value="attachments" data-testid="tab-attachments" className="gap-2">
            <Paperclip size={14} weight="bold" /> Anexos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inspections">
          <div className="tech-card overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Missão</th>
                  <th className="label-tech text-muted-foreground p-4">Data</th>
                  <th className="label-tech text-muted-foreground p-4">Local</th>
                  <th className="label-tech text-muted-foreground p-4">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {inspections.length === 0 ? (
                  <tr><td colSpan={5} className="p-12 text-center text-muted-foreground">Nenhuma inspeção</td></tr>
                ) : inspections.map((r) => (
                  <tr key={r.id} className="border-b border-border hover:bg-secondary/30">
                    <td className="p-4"><div className="font-bold">{r.title}</div></td>
                    <td className="p-4 text-sm mono">{formatDateTime(r.scheduled_start)}</td>
                    <td className="p-4 text-sm">{r.address_city ? `${r.address_city}/${r.address_state || ""}` : "—"}</td>
                    <td className="p-4 text-sm mono">{r.price ? formatBRL(r.price) : "—"}</td>
                    <td className="p-4"><Badge className={INSP_STATUS[r.status]?.cls}>{INSP_STATUS[r.status]?.label}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <div className="tech-card overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="label-tech text-muted-foreground p-4">Nº</th>
                  <th className="label-tech text-muted-foreground p-4">Descrição</th>
                  <th className="label-tech text-muted-foreground p-4">Emissão</th>
                  <th className="label-tech text-muted-foreground p-4">Vencimento</th>
                  <th className="label-tech text-muted-foreground p-4 text-right">Valor</th>
                  <th className="label-tech text-muted-foreground p-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">Nenhuma fatura</td></tr>
                ) : invoices.map((r) => (
                  <tr key={r.id} className="border-b border-border hover:bg-secondary/30">
                    <td className="p-4 mono text-sm">{r.number || "—"}</td>
                    <td className="p-4">{r.description}</td>
                    <td className="p-4 text-sm mono">{formatDate(r.issue_date)}</td>
                    <td className="p-4 text-sm mono">{formatDate(r.due_date)}</td>
                    <td className="p-4 text-right mono font-bold">{formatBRL(r.amount)}</td>
                    <td className="p-4"><Badge className={INV_STATUS[r.status]?.cls}>{INV_STATUS[r.status]?.label}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {["proposals", "contracts"].map((tab) => {
          const list = tab === "proposals" ? proposals : contracts;
          return (
            <TabsContent key={tab} value={tab}>
              <div className="tech-card overflow-hidden">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left">
                      <th className="label-tech text-muted-foreground p-4">Nº</th>
                      <th className="label-tech text-muted-foreground p-4">Título</th>
                      <th className="label-tech text-muted-foreground p-4">Valor</th>
                      <th className="label-tech text-muted-foreground p-4">Data</th>
                      <th className="label-tech text-muted-foreground p-4">Status</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">
                        Nenhum{tab === "proposals" ? "a proposta" : " contrato"}
                      </td></tr>
                    ) : list.map((d) => (
                      <tr key={d.id} className="border-b border-border hover:bg-secondary/30">
                        <td className="p-4 mono text-sm">{d.number || "—"}</td>
                        <td className="p-4 font-bold">{d.title}</td>
                        <td className="p-4 text-sm mono">{d.amount ? formatBRL(d.amount) : "—"}</td>
                        <td className="p-4 text-sm mono">{formatDate(d.created_at)}</td>
                        <td className="p-4"><Badge className={DOC_STATUS[d.status]?.cls}>{DOC_STATUS[d.status]?.label}</Badge></td>
                        <td className="p-4 text-right">
                          <Button size="icon" variant="ghost" onClick={() => window.open(`${API}/documents/${d.id}/pdf`, "_blank")}>
                            <FilePdf size={16} className="text-accent" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          );
        })}

        <TabsContent value="attachments">
          <Attachments clientId={id} title="Documentos anexados" />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
